import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceFolderCloseIcon, WorkspaceFolderOpenIcon } from "assets/material-custom-icons";
import { IFolder } from "store/types";

interface Props {
  folders: IFolder[];
  folder: IFolder;
  selectedFolderId: number | null;
  onSelectFolder: (folder: IFolder) => void;
  level?: number;
}

export const Folder: React.FC<Props> = ({ folders, folder, selectedFolderId, onSelectFolder, level = 0 }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const childrenFolders = folders.filter((f) => f.parent_id === folder.id);
  const isSelected = selectedFolderId === folder.id;

  const hasChildren = Boolean(childrenFolders.length);

  let clickTimeout: NodeJS.Timeout | null = null;

  const handleSelectFolder = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".expand-icon")) return;

    if (clickTimeout !== null) {
      if (hasChildren) {
        setExpanded(!expanded);
      }
      clearTimeout(clickTimeout);
      clickTimeout = null;
    } else {
      clickTimeout = setTimeout(() => {
        onSelectFolder(folder);
        clickTimeout && clearTimeout(clickTimeout);
        clickTimeout = null;
      }, 200);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      sx={{
        boxShadow: "none",
        // "&:before": { display: "none" },
        // borderBottom: "1px solid #DAE0E2",
        // backgroundColor: isSelected ? "#DBE8F5" : "transparent",
        // "&:hover": {
        //   backgroundColor: isSelected ? "#DBE8F5" : "#f5f9fa"
        // },
        "&.Mui-expanded": {
          margin: 0
        }
      }}
    >
      <AccordionSummary
        sx={{
          overflow: "hidden",
          padding: 0,
          height: 39,
          minHeight: "39px !important",
          maxHeight: "39px !important",
          backgroundColor: isSelected ? "#DBE8F5" : "transparent",
          paddingLeft: `${level * 24}px`,
          "&:hover": {
            backgroundColor: isSelected ? "#DBE8F5" : "#f5f9fa"
          },
          "&.Mui-expanded": {
            height: "39px !important",
            minHeight: "39px !important",
            maxHeight: "39px !important"
          },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            overflow: "hidden",
            "&.Mui-expanded": {
              margin: 0
            }
          }
        }}
        onClick={handleSelectFolder}
      >
        <Stack sx={{ width: "100%", flexDirection: "row", alignItems: "center", gap: "10px", overflow: "hidden" }}>
          <Stack sx={{ flexDirection: "row", alignItems: "center" }}>
            {hasChildren ? (
              <IconButton className="expand-icon" onClick={() => setExpanded(!expanded)}>
                <ExpandMoreIcon sx={{ fill: "#0A3142", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }} />
              </IconButton>
            ) : (
              <Box sx={{ width: 32, height: 32 }} />
            )}
            {expanded && hasChildren ? (
              <WorkspaceFolderOpenIcon sx={{ fill: "#0A3142" }} />
            ) : (
              <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142" }} />
            )}
          </Stack>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#0a3142",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              flex: 1
            }}
          >
            {folder.title}
          </Typography>
        </Stack>
      </AccordionSummary>
      {Boolean(childrenFolders.length) && (
        <AccordionDetails sx={{ padding: 0 }}>
          {childrenFolders.map((childFolder) => (
            <Folder
              key={childFolder.id}
              folders={folders}
              folder={childFolder}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              level={level + 1}
            />
          ))}
        </AccordionDetails>
      )}
    </Accordion>
  );
};
